// File: frontend/src/components/ui/DestinationTabs.js
//
// ONE TAB STRIP FOR THE WHOLE ADMIN APP.
//
// WHY IT EXISTS
//   The 2026-08-20 information-architecture restructure collapsed several
//   sidebar rows into tabs on the destination that owns the job — Sales and
//   Transactions became one page, the Analytics tabs went to the pages whose
//   questions they answer, Remote Access and the portal editor moved inside
//   Router. That trade is only worth making if a tab strip is the same control
//   everywhere. Four hand-rolled strips with four sets of sx, four different
//   heights and four different ideas about aria is how "simpler navigation"
//   turns into a product that feels assembled rather than designed.
//
// WHAT IT GUARANTEES
//   · one height, one weight, one indicator, on every page;
//   · scrollable on a phone with the buttons enabled, so a fifth tab is never
//     hidden behind a swipe nobody discovers;
//   · the real ARIA pairing — `role="tab"` carries the id the panel names, and
//     `<TabPanel>` below carries the id the tab controls. A tab that promises a
//     panel and never names one is silently ignored by a screen reader and looks
//     exactly like the defect it was meant to prevent;
//   · ONE tab renders no strip at all. A control offering a choice that does not
//     exist is furniture — and on a permission-filtered page it would also be the
//     only thing on screen advertising a surface the reader cannot open.
//
// WHAT IT IS NOT
//   Not a router and not a data owner. The host page keeps the tab in its own
//   state (normally the URL, so a tab is linkable and a deep link survives), and
//   mounts exactly one panel's subtree — never all of them hidden, which would
//   run every panel's requests for content nobody is looking at.

import React from "react";
import { Box, Tab, Tabs } from "@mui/material";

/**
 * @param {string}  id       unique per page; namespaces the tab/panel element ids
 * @param {Array}   tabs     [{ id, label, icon? }] — already filtered by permission
 * @param {string}  value    the open tab's id
 * @param {Func}    onChange (nextId) => void
 * @param {string}  [ariaLabel] names the tab list, e.g. "Packages sections"
 */
export default function DestinationTabs({ id, tabs, value, onChange, ariaLabel, sx }) {
  if (!Array.isArray(tabs) || tabs.length < 2) return null;

  // A tab entry without an `id` is silently catastrophic, not cosmetic: MUI
  // falls back to the child INDEX when a Tab's value is undefined, so the host
  // page's string comparisons stop matching and its panel renders NOTHING —
  // no loading, no empty state, no error. That shipped once, in the Agent
  // detail drawer, where every entry said `value:` instead of `id:`. Say so
  // out loud in development rather than letting the next one ship blank.
  if (process.env.NODE_ENV !== "production") {
    const bad = tabs.filter((t) => !t || typeof t.id !== "string" || !t.id);
    if (bad.length) {
      // eslint-disable-next-line no-console
      console.error(
        `DestinationTabs("${id}"): ${bad.length} tab(s) have no string \`id\`. ` +
        "The contract is { id, label, icon? } — an entry keyed `value` leaves " +
        "t.id undefined and the panel will render blank.",
      );
    }
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: { xs: 2, sm: 2.5 }, ...sx }}>
      <Tabs
        value={value}
        onChange={(_e, v) => onChange?.(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label={ariaLabel || "Sections"}
        sx={{
          minHeight: 44,
          "& .MuiTab-root": {
            minHeight: 44, textTransform: "none", fontWeight: 700, fontSize: 13.5,
            minWidth: "auto", px: { xs: 1.5, sm: 2 },
          },
          "& .MuiTab-iconWrapper": { mr: 0.75 },
          "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" },
        }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.id}
            value={t.id}
            id={`${id}-tab-${t.id}`}
            aria-controls={`${id}-panel`}
            icon={t.icon ? <t.icon sx={{ fontSize: 18 }} /> : undefined}
            iconPosition={t.icon ? "start" : undefined}
            label={t.label}
          />
        ))}
      </Tabs>
    </Box>
  );
}

/**
 * The other half of the pairing. Renders a plain region when there is only one
 * tab — a lone panel has no tab to be labelled by, and claiming otherwise points
 * a screen reader at an element that does not exist.
 */
export function TabPanel({ id, value, single = false, children, sx }) {
  return (
    <Box
      role={single ? undefined : "tabpanel"}
      id={single ? undefined : `${id}-panel`}
      aria-labelledby={single ? undefined : `${id}-tab-${value}`}
      sx={{ width: "100%", minWidth: 0, ...sx }}
    >
      {children}
    </Box>
  );
}
